/* eslint-disable indent */
import Card from '@/components/Card';
import { Button } from '@/components/Button';
import { FormField } from '@/components/Form/FormField';
import { useEffect, useState } from 'react';
import { Controller, FieldValues, Path, PathValue } from 'react-hook-form';
import { Select } from '@/components/Controls/Select';
import { FormManagementProps } from '@/type/form.types';
import {
  useGetGroupById,
  useGetGroupsSelectOptions,
} from '@/hooks/group.hooks';
import {
  useGetCompaniesSelectOptions,
  useGetCompanyById,
} from '@/hooks/company.hooks';
import {
  useGetBranchById,
  useGetBranchesSelectOptions,
} from '@/hooks/branch.hooks';
import {
  useCreateVehicule,
  useGetExistingVehicule,
  useGetVehiculesSelectOptions,
  useUpdateVehicule,
} from '@/hooks/vehicule.hooks';
import { Switch } from '@/components/Controls/Switch';
import { TitleSection } from '@/components/Header/Section/TitleSection';
import { NotFound } from '@/components/Message/NotFound';
import { allFieldErrorsEmpty, resetFieldError } from '@/utils/object.utils';
import {
  EMAIL_REGEX,
  FRENCH_LICENSE_PLATE_NUMBER_REGEX,
  PHONE_REGEX,
  SPACE_REGEX,
  VIN_REGEX,
} from '@/utils/regex.utils';
import { Input } from '@/components/Controls/Input';
import Loader from '@/components/Loading/Loader';
import { FieldError } from '@/type/error.types';
import { FormSectionTitle } from '@/components/Form/FormSectionTitle';
import { useGetVehiculeCheckTypesSelectOptions } from '@/hooks/vehicule-check-type.hooks';
import { ActionUserType } from '@/api/entities/vehicule-check';
import { RichTextEditor } from '@/components/Custom/RichTextEditor';
import { twMerge } from 'tailwind-merge';
import { containsOnlyEmptyHTMLTags } from '@/utils/string.utils';
import Modal from '@/components/Modal';
import { showModal } from '@/utils/modal.utils';
import { WarningMessage } from '@/components/Message/WarningMessage';
import { TVehiculeCheckEmailSchema } from '@/schemas/vehicule-check-email/vehicule-check-email.schema';
import { useUserRole } from '@/hooks/auth.hooks';
import { useParams } from 'react-router-dom';
import { generateRandomUUID } from '@/utils/random-uuid.utils';
import { SelectAutoComplete } from '@/components/Controls/SelectAutoComplete';

interface VehiculeCheckRequestProps<TSchema extends FieldValues>
  extends FormManagementProps<TSchema> {
  onCreateEmail?: (data: TVehiculeCheckEmailSchema) => void;
}

export const VehiculeCheckRequestManagement = <TSchema extends FieldValues>({
  t,
  data,
  watch,
  control,
  register,
  setValue,
  setError,
  errors,
  submitDisabled,
  onSubmit,
  onCreateEmail,
}: VehiculeCheckRequestProps<TSchema>) => {
  const { vehiculeId } = useParams();

  const [assignedToVehicule, setAssignedToVehicule] = useState(
    Boolean(vehiculeId) || Boolean(data?.vehiculeId)
  );
  const [licensePlateNumberValue, setLicensePlateNumberValue] = useState('');
  const [vinValue, setVinValue] = useState('');
  const [licensePlateNumberError, setLicensePlateNumberError] = useState('');
  const [vinError, setVinError] = useState('');
  const [brandValue, setBrandValue] = useState('');
  const [brandError, setBrandError] = useState('');
  const [modelValue, setModelValue] = useState('');
  const [modelError, setModelError] = useState('');
  const [toSendByEmail, setToSendByEmail] = useState(false);
  const [canSendEmail, setCanSendEmail] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const [originalEmailContent, setOriginalEmailContent] = useState('');

  const { isAdmin, isGroupManager, isCompanyManager, isBranchManager } =
    useUserRole();

  const canAccessGroups = isAdmin || isGroupManager;
  const canAccessCompanies = isAdmin || isGroupManager || isCompanyManager;
  const canAccessBranches =
    isAdmin || isGroupManager || isCompanyManager || isBranchManager;

  const { data: existingVehicule } = useGetExistingVehicule({
    id: data?.id,
    vin: vinValue,
    licensePlateNumber: licensePlateNumberValue,
  });

  /**
   * === Set Value Watchers section ===
   */
  const referenceNumberValue =
    watch &&
    watch('referenceNumber' as Path<TSchema>, data?.referenceNumber ?? '');

  const groupIdValue =
    watch && watch('groupId' as Path<TSchema>, data?.groupId ?? '');

  const companyIdValue =
    watch && watch('companyId' as Path<TSchema>, data?.companyId ?? '');

  const branchIdValue =
    watch && watch('branchId' as Path<TSchema>, data?.branchId ?? '');

  const vehiculeIdValue =
    watch && watch('vehiculeId' as Path<TSchema>, data?.vehiculeId ?? '');

  const lastnameValue =
    watch &&
    watch(
      'individualLastname' as Path<TSchema>,
      data?.individualLastname ?? ''
    );

  const firstnameValue =
    watch &&
    watch(
      'individualFirstname' as Path<TSchema>,
      data?.individualFirstname ?? ''
    );

  const emailValue =
    watch &&
    watch('individualEmail' as Path<TSchema>, data?.individualEmail ?? '');

  const phoneValue =
    watch &&
    watch('individualPhone' as Path<TSchema>, data?.individualPhone ?? '');

  const emailSubjectValue =
    watch &&
    watch(
      'individualEmailSubject' as Path<TSchema>,
      data?.individualEmailSubject ?? ''
    );

  const emailContentValue =
    watch &&
    watch(
      'individualEmailContent' as Path<TSchema>,
      data?.individualEmailContent ?? ''
    );

  const addressLine1Value =
    watch &&
    watch(
      'individualAddressLine1' as Path<TSchema>,
      data?.individualAddressLine1 ?? ''
    );

  const addressLine2Value =
    watch &&
    watch(
      'individualAddressLine2' as Path<TSchema>,
      data?.individualAddressLine2 ?? ''
    );

  const postalCodeValue =
    watch &&
    watch(
      'individualPostalCode' as Path<TSchema>,
      data?.individualPostalCode ?? ''
    );

  const cityValue =
    watch &&
    watch('individualCity' as Path<TSchema>, data?.individualCity ?? '');

  const countryValue =
    watch &&
    watch('individualCountry' as Path<TSchema>, data?.individualCountry ?? '');
  /**
   * === END OF Set Value Watchers section ===
   */

  const { getGroupsSelectOptions, isLoadingAllGroups } =
    useGetGroupsSelectOptions();

  const { getCompaniesSelectOptions, isLoadingAllCompanies } =
    useGetCompaniesSelectOptions({
      groupId: groupIdValue,
    });

  const { getBranchesSelectOptions, isLoadingAllBranches } =
    useGetBranchesSelectOptions({
      groupId: groupIdValue,
      companyId: companyIdValue,
    });

  const { getAllVehiculesSelectOptions, isLoadingAllVehicules } =
    useGetVehiculesSelectOptions({
      groupId: groupIdValue,
      companyId: companyIdValue,
    });

  const { getVehiculeCheckTypesSelectOptions, isLoadingAllVehiculeCheckTypes } =
    useGetVehiculeCheckTypesSelectOptions({
      groupId: groupIdValue,
      personTypes: [ActionUserType.INDIVIDUAL],
    });

  const numberOfCompanies = getCompaniesSelectOptions()?.length;
  const numberOfBranches = getBranchesSelectOptions()?.length;
  const numberOfVehicules = getAllVehiculesSelectOptions()?.length;
  const numberOfVehiculeCheckTypes =
    getVehiculeCheckTypesSelectOptions()?.length;

  const { data: selectedGroup, isLoading: isLoadingGroup } =
    useGetGroupById(groupIdValue);

  const { data: selectedCompany, isLoading: isLoadingCompany } =
    useGetCompanyById(companyIdValue);

  const { data: selectedBranch, isLoading: isLoadingBranch } =
    useGetBranchById(branchIdValue);

  const { mutate: mutateUpdateVehicule, isPending: isPendingUpdateVehicule } =
    useUpdateVehicule({ t, showDefaultSuccessMessage: false });

  const { mutate: mutateCreateVehicule, isPending: isPendingCreateVehicule } =
    useCreateVehicule({
      t,
      showDefaultSuccessMessage: false,
      handleSuccess: (data) => {
        setValue('vehiculeId' as Path<TSchema>, data?.['data']?.id);

        setLicensePlateNumberValue('');
        setVinValue('');
        setBrandValue('');
        setModelValue('');

        if (allFieldErrorsEmpty(errors)) {
          getEmailDetails();
          onSubmit?.();
        }
      },
    });

  /**
   * === Set Fields Initial Values section ===
   */
  useEffect(() => {
    if (vehiculeId || data?.vehiculeId) {
      setValue(
        'vehiculeId' as Path<TSchema>,
        (vehiculeId || data?.vehiculeId) ?? ''
      );
    }
  }, [vehiculeId, data, setValue]);

  useEffect(() => {
    if (setValue && data) {
      setValue('referenceNumber' as Path<TSchema>, data.referenceNumber ?? '');
      setValue('groupId' as Path<TSchema>, data.groupId ?? '');
      setValue('companyId' as Path<TSchema>, data.companyId ?? '');
      setValue('branchId' as Path<TSchema>, data.branchId ?? '');

      setValue(
        'individualLastname' as Path<TSchema>,
        data.individualLastname ?? ''
      );

      setValue(
        'individualFirstname' as Path<TSchema>,
        data.individualFirstname ?? ''
      );

      setValue('individualEmail' as Path<TSchema>, data.individualEmail ?? '');

      setValue('individualPhone' as Path<TSchema>, data.individualPhone ?? '');

      setValue(
        'individualAddressLine1' as Path<TSchema>,
        data.individualAddressLine1 ?? ''
      );

      setValue(
        'individualAddressLine2' as Path<TSchema>,
        data.individualAddressLine2 ?? ''
      );

      setValue(
        'individualPostalCode' as Path<TSchema>,
        data.individualPostalCode ?? ''
      );

      setValue('individualCity' as Path<TSchema>, data.individualCity ?? '');

      setValue(
        'individualCountry' as Path<TSchema>,
        data.individualCountry ?? ''
      );
    }
  }, [setValue, data]);
  /**
   * === END OF Set Fields Initial Values section ===
   */

  /**
   * === Set Email Subject and Content section ===
   */
  useEffect(() => {
    if (toSendByEmail && canSendEmail && groupIdValue && selectedGroup) {
      setValue(
        'individualEmailSubject' as Path<TSchema>,
        (data?.emails?.[0]?.emailSubject ||
          selectedGroup?.data?.individualEmailSubject) ??
          ''
      );

      const contentTemplate =
        (data?.emails?.[0]?.emailContent ||
          selectedGroup?.data?.individualEmailContent) ??
        '';

      setOriginalEmailContent(contentTemplate);

      // Replace all variables if values already exist
      const contentWithAllVariables = replaceEmailVariables(contentTemplate);

      setValue(
        'individualEmailContent' as Path<TSchema>,
        contentWithAllVariables as PathValue<TSchema, Path<TSchema>>
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    toSendByEmail,
    canSendEmail,
    groupIdValue,
    selectedGroup,
    selectedCompany,
    selectedBranch,
    referenceNumberValue,
    firstnameValue,
    lastnameValue,
    addressLine1Value,
    addressLine2Value,
    postalCodeValue,
    cityValue,
    countryValue,
    data?.emails,
    setValue,
  ]);
  /**
   * === END OF Set Email Subject and Content section ===
   */

  /**
   * === Update Variables in Email Content ===
   */
  useEffect(() => {
    if (originalEmailContent) {
      const updatedContent = replaceEmailVariables(originalEmailContent);

      setValue(
        'individualEmailContent' as Path<TSchema>,
        updatedContent as PathValue<TSchema, Path<TSchema>>
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    toSendByEmail,
    originalEmailContent,
    referenceNumberValue,
    firstnameValue,
    lastnameValue,
    addressLine1Value,
    addressLine2Value,
    postalCodeValue,
    cityValue,
    countryValue,
    selectedGroup,
    selectedCompany,
    selectedBranch,
    setValue,
  ]);
  /**
   * === END OF Update Variables in Email Content ===
   */

  /**
   * === Generate UUID for Reference Number ===
   */
  useEffect(() => {
    const referenceNumber = data?.referenceNumber || generateRandomUUID();

    setValue(
      'referenceNumber' as Path<TSchema>,
      referenceNumber as PathValue<TSchema, Path<TSchema>>
    );
  }, [data?.referenceNumber, setValue]);
  /**
   * === END OF Generate UUID for Reference Number ===
   */

  /**
   * === Vehicule Select Functionality section ===
   */
  useEffect(() => {
    if (!assignedToVehicule) {
      setValue(
        'vehiculeId' as Path<TSchema>,
        '' as PathValue<TSchema, Path<TSchema>>
      );
    } else {
      if (vehiculeId || data?.vehiculeId) {
        setValue('vehiculeId' as Path<TSchema>, vehiculeId || data?.vehiculeId);
      }
      setLicensePlateNumberValue('');
      setVinValue('');
      setBrandValue('');
      setModelValue('');
    }
  }, [assignedToVehicule, setValue, vehiculeId, data?.vehiculeId]);
  /**
   * === END OF Vehicule Select Functionality section ===
   */

  /**
   * === Reset Error Messages On Value Change section ===
   */
  useEffect(() => {
    if (groupIdValue) {
      resetFieldError<TSchema>({ fieldName: 'groupId', setError });
    }

    if (referenceNumberValue) {
      resetFieldError<TSchema>({ fieldName: 'referenceNumber', setError });
    }

    if (vehiculeIdValue) {
      resetFieldError<TSchema>({ fieldName: 'vehiculeId', setError });
    }

    if (licensePlateNumberValue) {
      setLicensePlateNumberError('');
    }

    if (vinValue) {
      setVinError('');
    }

    if (brandValue) {
      setBrandError('');
    }

    if (modelValue) {
      setModelError('');
    }

    if (lastnameValue) {
      resetFieldError<TSchema>({ fieldName: 'individualLastname', setError });
    }

    if (firstnameValue) {
      resetFieldError<TSchema>({ fieldName: 'individualFirstname', setError });
    }

    if (emailValue) {
      resetFieldError<TSchema>({ fieldName: 'individualEmail', setError });
    }

    if (phoneValue) {
      resetFieldError<TSchema>({ fieldName: 'individualPhone', setError });
    }

    if (emailSubjectValue) {
      resetFieldError<TSchema>({
        fieldName: 'individualEmailSubject',
        setError,
      });
    }

    if (emailContentValue) {
      resetFieldError<TSchema>({
        fieldName: 'individualEmailContent',
        setError,
      });
    }
  }, [
    groupIdValue,
    referenceNumberValue,
    licensePlateNumberValue,
    vinValue,
    brandValue,
    modelValue,
    vehiculeIdValue,
    lastnameValue,
    firstnameValue,
    emailValue,
    phoneValue,
    emailSubjectValue,
    emailContentValue,
    setError,
  ]);
  /**
   * === END OF Reset Error Messages On Value Change section ===
   */

  useEffect(() => {
    if (groupIdValue && selectedGroup) {
      const groupCanSendEmail =
        selectedGroup?.data?.isWhiteLabel && selectedGroup?.data?.fromEmail;

      setCanSendEmail(groupCanSendEmail ?? false);
    } else {
      setCanSendEmail(false);
    }
  }, [groupIdValue, selectedGroup]);

  useEffect(() => {
    if (toSendByEmail) {
      if (!groupIdValue) {
        setWarningMessage(t('pleaseSelectAGroup'));
        showModal('#information-modal');
        setToSendByEmail(false);
      } else if (!canSendEmail) {
        setWarningMessage(t('selectedGroupNotHavingSenderEmail'));
        showModal('#information-modal');
        setToSendByEmail(false);
      }
    }
  }, [toSendByEmail, canSendEmail, groupIdValue, t]);

  const replaceEmailVariables = (template: string) => {
    return template
      .replace(/\{\{referenceNumber\}\}/g, referenceNumberValue || '')
      .replace(/\{\{firstname\}\}/g, firstnameValue || '')
      .replace(/\{\{lastname\}\}/g, lastnameValue || '')
      .replace(/\{\{addressLine1\}\}/g, addressLine1Value || '')
      .replace(/\{\{addressLine2\}\}/g, addressLine2Value || '')
      .replace(/\{\{postalCode\}\}/g, postalCodeValue || '')
      .replace(/\{\{city\}\}/g, cityValue || '')
      .replace(/\{\{country\}\}/g, countryValue || '')
      .replace(/\{\{groupEmail\}\}/g, selectedGroup?.data?.email || '')
      .replace(/\{\{groupPhone\}\}/g, selectedGroup?.data?.phone || '')
      .replace(/\{\{companyEmail\}\}/g, selectedCompany?.data?.email || '')
      .replace(/\{\{companyPhone\}\}/g, selectedCompany?.data?.phone || '')
      .replace(/\{\{branchEmail\}\}/g, selectedBranch?.data?.email || '')
      .replace(
        /\{\{branchPrimaryPhone\}\}/g,
        selectedBranch?.data?.primaryPhone || ''
      )
      .replace(
        /\{\{branchSecondaryPhone\}\}/g,
        selectedBranch?.data?.secondaryPhone || ''
      );
  };

  const getEmailDetails = () => {
    if (toSendByEmail && emailSubjectValue && emailContentValue) {
      onCreateEmail({
        toLastname: lastnameValue,
        toFirstname: firstnameValue,
        toEmail: emailValue,
        emailSubject: emailSubjectValue,
        emailContent: emailContentValue,
      });
    }
  };

  /**
   * === Handle Submit section ===
   */
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!referenceNumberValue) {
      setError('referenceNumber' as Path<TSchema>, {
        message: t('referenceNumberRequired'),
      });
    }

    if (!groupIdValue) {
      setError('groupId' as Path<TSchema>, {
        message: t('common:groupRequired'),
      });
    }

    if (assignedToVehicule) {
      if (!vehiculeIdValue) {
        setError('vehiculeId' as Path<TSchema>, {
          message: t('common:vehiculeRequired'),
        });
      } else {
        mutateUpdateVehicule({
          id: vehiculeIdValue,
          ownerGroupId: groupIdValue,
          ownerCompanyId: companyIdValue,
        });
      }
    } else {
      const vehiculeErrorMessages = existingVehicule?.data?.errorMessages;
      let hasLicensePlateError = false;
      let hasVinError = false;
      let hasBrandError = false;
      let hasModelError = false;

      if (licensePlateNumberValue) {
        if (!FRENCH_LICENSE_PLATE_NUMBER_REGEX.test(licensePlateNumberValue)) {
          setLicensePlateNumberError(
            t('common:licensePlateNumberFrenchIncorrect')
          );
          hasLicensePlateError = true;
        } else if (
          vehiculeErrorMessages?.includes(
            FieldError.LICENSE_PLATE_NUMBER_EXISTS
          )
        ) {
          setLicensePlateNumberError(
            t('common:vehiculeLicensePlateNumberAlreadyExists')
          );
          hasLicensePlateError = true;
        }
      }

      if (vinValue) {
        if (!VIN_REGEX.test(vinValue)) {
          setVinError(t('common:vinIncorrect'));
          hasVinError = true;
        } else if (vehiculeErrorMessages?.includes(FieldError.VIN_EXISTS)) {
          setVinError(t('common:vehiculeVinAlreadyExists'));
          hasVinError = true;
        }
      }

      if (!brandValue) {
        setBrandError(t('common:brandRequired'));
        hasBrandError = true;
      }

      if (!modelValue) {
        setModelError(t('common:modelRequired'));
        hasModelError = true;
      }

      if (
        !hasLicensePlateError &&
        !hasVinError &&
        !hasBrandError &&
        !hasModelError
      ) {
        mutateCreateVehicule({
          licensePlateNumber: licensePlateNumberValue,
          vin: vinValue,
          brand: brandValue,
          model: modelValue,
          ownerGroupId: groupIdValue,
          ownerCompanyId: companyIdValue,
        });
      }
    }

    if (!lastnameValue) {
      setError('individualLastname' as Path<TSchema>, {
        message: t('common:lastnameRequired'),
      });
    }

    if (!firstnameValue) {
      setError('individualFirstname' as Path<TSchema>, {
        message: t('common:firstnameRequired'),
      });
    }

    if (!emailValue) {
      setError('individualEmail' as Path<TSchema>, {
        message: t('common:emailRequired'),
      });
    } else if (!EMAIL_REGEX.test(emailValue)) {
      setError('individualEmail' as Path<TSchema>, {
        message: t('common:emailFormatInvalid'),
      });
    }

    if (phoneValue && !PHONE_REGEX.test(phoneValue)) {
      setError('individualPhone' as Path<TSchema>, {
        message: t('common:phoneNumberIncorrect'),
      });
    }

    if (toSendByEmail) {
      if (!emailSubjectValue) {
        setError('individualEmailSubject' as Path<TSchema>, {
          message: t('common:emailSubjectRequired'),
        });
      }

      if (!emailContentValue || containsOnlyEmptyHTMLTags(emailContentValue)) {
        setError('individualEmailContent' as Path<TSchema>, {
          message: t('common:emailContentRequired'),
        });
      }
    }

    if (assignedToVehicule && allFieldErrorsEmpty(errors)) {
      getEmailDetails();
      onSubmit?.();
    }
  };
  /**
   * === END OF Handle Submit section ===
   */

  return (
    <>
      <form onSubmit={handleSubmit}>
        <Card
          title={
            <TitleSection
              text={t('common:informations')}
              defaultIconClassName="ki-badge"
            />
          }
          footer={
            <Button
              buttonClassName="px-8"
              text={
                submitDisabled ||
                isPendingCreateVehicule ||
                isPendingUpdateVehicule
                  ? `${t('common:loading')}...`
                  : toSendByEmail
                  ? t('common:send')
                  : t('common:save')
              }
              disabled={
                submitDisabled ||
                isPendingCreateVehicule ||
                isPendingUpdateVehicule
              }
            />
          }
          bodyClassName="py-8 flex flex-col gap-5"
        >
          <>
            <FormSectionTitle
              title={t('theInspection')}
              containerClassName="mt-5"
              titleClassName="font-semibold"
              icon={
                <i className="h-0 ki-outline ki-notepad !text-[25px] -mt-0.75" />
              }
            />
            <FormField
              text={t('common:referenceNumber_short_2')}
              required
              textWrapperClassName="mt-3"
            >
              <div className="flex flex-1 flex-wrap items-start gap-5">
                <Input
                  {...register('referenceNumber' as Path<TSchema>, {
                    setValueAs: (value: string) =>
                      value.replace(SPACE_REGEX, ''),
                  })}
                  errorMessage={
                    errors && (errors.referenceNumber?.message as string)
                  }
                  // Enforce to input only value to be without space
                  onInput={(e) =>
                    (e.currentTarget.value = e.currentTarget.value.replace(
                      SPACE_REGEX,
                      ''
                    ))
                  }
                  placeholder={t('common:referenceNumber_short_2')}
                  type="text"
                />
              </div>
            </FormField>
            {canAccessGroups && (
              <FormField
                text={t('common:group')}
                required
                textWrapperClassName="mt-3"
              >
                <div className="flex flex-1 flex-wrap items-start gap-5">
                  {isLoadingAllGroups ? (
                    <Select
                      placeholder={t('common:chooseTheGroup')}
                      options={[]}
                    />
                  ) : (
                    <Controller
                      name="groupId"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          errorMessage={
                            errors && (errors.groupId?.message as string)
                          }
                          placeholder={t('common:chooseTheGroup')}
                          options={getGroupsSelectOptions()}
                        />
                      )}
                    />
                  )}
                </div>
              </FormField>
            )}
            {canAccessCompanies && (
              <FormField text={t('common:company')} textWrapperClassName="mt-3">
                <div className="flex flex-1 flex-wrap items-start gap-5">
                  {isLoadingAllCompanies ? (
                    <Select
                      placeholder={t('common:chooseTheCompany')}
                      options={[]}
                    />
                  ) : !numberOfCompanies ? (
                    <div className="mt-3">
                      <NotFound
                        containerClassName="gap-1.5"
                        iconClassName="-mt-1.5 text-lg"
                        textClassName="text-sm"
                        text={t('common:noCompanyLinkedToThisGroup')}
                      />
                    </div>
                  ) : (
                    <Controller
                      name="companyId"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          errorMessage={
                            errors && (errors.companyId?.message as string)
                          }
                          placeholder={t('common:chooseTheCompany')}
                          options={getCompaniesSelectOptions()}
                        />
                      )}
                    />
                  )}
                </div>
              </FormField>
            )}
            {canAccessBranches && (
              <FormField text={t('common:branch')} textWrapperClassName="mt-3">
                <div className="flex flex-1 flex-wrap items-start gap-5">
                  {isLoadingAllBranches ? (
                    <Select
                      placeholder={t('common:chooseTheBranch')}
                      options={[]}
                    />
                  ) : !numberOfBranches ? (
                    <div className="mt-3">
                      <NotFound
                        containerClassName="gap-1.5"
                        iconClassName="-mt-1.5 text-lg"
                        textClassName="text-sm"
                        text={
                          groupIdValue
                            ? t('common:noBranchLinkedToThisGroup')
                            : t('common:noBranchLinkedToThisCompany')
                        }
                      />
                    </div>
                  ) : (
                    <Controller
                      name="branchId"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          errorMessage={
                            errors && (errors.branchId?.message as string)
                          }
                          placeholder={t('common:chooseTheBranch')}
                          options={getBranchesSelectOptions()}
                        />
                      )}
                    />
                  )}
                </div>
              </FormField>
            )}
            <FormField
              text={`${t('onAVehiculeOfTheFleet')} ?`}
              textWrapperClassName="mt-2"
            >
              <div className="flex flex-1 flex-wrap items-start gap-5 mt-2">
                <Switch
                  onChange={() =>
                    setAssignedToVehicule((prevState) => !prevState)
                  }
                  checked={assignedToVehicule}
                />
              </div>
            </FormField>
            {assignedToVehicule ? (
              <FormField
                text={t('common:vehicule')}
                required={assignedToVehicule && numberOfVehicules > 0}
                textWrapperClassName="mt-5"
              >
                <div className="flex flex-1 flex-wrap items-start gap-5 mt-2">
                  {isLoadingAllVehicules ? (
                    <Select
                      placeholder={t('common:chooseTheVehicule')}
                      options={[]}
                    />
                  ) : !numberOfVehicules ? (
                    <div className="mt-3">
                      <NotFound
                        containerClassName="gap-1.5"
                        iconClassName="-mt-1.5 text-lg"
                        textClassName="text-sm"
                        text={
                          branchIdValue
                            ? t('noVehiculeBelongsToThisBranch')
                            : companyIdValue
                            ? t('noVehiculeBelongsToThisCompany')
                            : t('noVehiculeBelongsToThisGroup')
                        }
                      />
                    </div>
                  ) : (
                    // <Controller
                    //   name="vehiculeId"
                    //   control={control}
                    //   render={({ field }) => (
                    //     <Select
                    //       {...field}
                    //       errorMessage={
                    //         errors && (errors.vehiculeId?.message as string)
                    //       }
                    //       placeholder={t('common:chooseTheVehicule')}
                    //       options={getAllVehiculesSelectOptions()}
                    //     />
                    //   )}
                    // />
                    <SelectAutoComplete
                      placeholder={t('common:chooseTheVehicule')}
                      name="vehiculeId"
                      control={control}
                      options={getAllVehiculesSelectOptions() ?? []}
                      disabled={false}
                      errorMessage={
                        errors && (errors.vehiculeId?.message as string)
                      }
                    />
                  )}
                </div>
              </FormField>
            ) : (
              <>
                <FormField
                  text={t('common:licensePlateNumber')}
                  textWrapperClassName="mt-3"
                >
                  <div className="flex flex-1 flex-wrap items-start gap-5">
                    <Input
                      value={licensePlateNumberValue}
                      onChange={(e) =>
                        setLicensePlateNumberValue(e.target.value)
                      }
                      errorMessage={licensePlateNumberError}
                      placeholder={t('common:licensePlateNumber')}
                      type="text"
                    />
                  </div>
                </FormField>
                <FormField text={t('common:vin')} textWrapperClassName="mt-3">
                  <div className="flex flex-1 flex-wrap items-start gap-5">
                    <Input
                      value={vinValue}
                      onChange={(e) => setVinValue(e.target.value)}
                      errorMessage={vinError}
                      placeholder={t('common:vin')}
                      type="text"
                    />
                  </div>
                </FormField>
                <FormField
                  text={t('common:brand')}
                  required
                  textWrapperClassName="mt-3"
                >
                  <div className="flex flex-1 flex-wrap items-start gap-5">
                    <Input
                      value={brandValue}
                      onChange={(e) => setBrandValue(e.target.value)}
                      errorMessage={brandError}
                      placeholder={t('common:brand')}
                      type="text"
                    />
                  </div>
                </FormField>
                <FormField
                  text={t('common:model')}
                  required
                  textWrapperClassName="mt-3"
                >
                  <div className="flex flex-1 flex-wrap items-start gap-5">
                    <Input
                      value={modelValue}
                      onChange={(e) => setModelValue(e.target.value)}
                      errorMessage={modelError}
                      placeholder={t('common:model')}
                      type="text"
                    />
                  </div>
                </FormField>
              </>
            )}
            <FormField
              text={`${t('whatTypeOfInspectionToPerform')} ?`}
              containerClassName="mt-7"
              textWrapperClassName="mt-3"
            >
              <div className="flex flex-1 flex-wrap items-start gap-5">
                {!isLoadingAllVehiculeCheckTypes &&
                !numberOfVehiculeCheckTypes ? (
                  <div className="mt-3">
                    <NotFound
                      containerClassName="gap-1.75"
                      iconClassName="-mt-1.25 text-xl"
                      textClassName="text-sm"
                      text={
                        groupIdValue
                          ? t('noInspectionTypeLinkedToThisGroup')
                          : t('youAreNotLinkedToAnyInspectionType')
                      }
                    />
                  </div>
                ) : (
                  <Controller
                    name="vehiculeCheckTypeId"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        errorMessage={
                          errors &&
                          (errors.vehiculeCheckTypeId?.message as string)
                        }
                        placeholder={t('common:chooseTheInspectionType')}
                        options={getVehiculeCheckTypesSelectOptions()}
                      />
                    )}
                  />
                )}
              </div>
            </FormField>
            <hr className="mt-7 mb-5" />
            <FormSectionTitle
              title={t('clientInformations')}
              titleClassName="font-semibold"
              icon={
                <i className="h-0 ki-outline ki-user-tick !text-[30px] -mt-1.5" />
              }
            />
            <FormField
              text={t('common:lastName')}
              required
              textWrapperClassName="mt-3"
            >
              <div className="flex flex-1 flex-wrap items-start gap-5">
                <Input
                  {...(register &&
                    register('individualLastname' as Path<TSchema>))}
                  errorMessage={
                    errors && (errors.individualLastname?.message as string)
                  }
                  placeholder={t('common:lastName')}
                  type="search"
                />
              </div>
            </FormField>
            <FormField
              text={t('common:firstName')}
              required
              textWrapperClassName="mt-3"
            >
              <div className="flex flex-1 flex-wrap items-start gap-5">
                <Input
                  {...(register &&
                    register('individualFirstname' as Path<TSchema>))}
                  errorMessage={
                    errors && (errors.individualFirstname?.message as string)
                  }
                  placeholder={t('common:firstName')}
                  type="search"
                />
              </div>
            </FormField>
            <FormField
              text={t('common:email')}
              required
              textWrapperClassName="mt-3"
            >
              <div className="flex flex-1 flex-wrap items-start gap-5">
                <Input
                  {...(register &&
                    register('individualEmail' as Path<TSchema>))}
                  errorMessage={
                    errors && (errors.individualEmail?.message as string)
                  }
                  placeholder={t('common:email')}
                  type="email"
                />
              </div>
            </FormField>
            <FormField
              text={t('common:phoneNumber')}
              textWrapperClassName="mt-3"
            >
              <div className="flex flex-1 flex-wrap items-start gap-5">
                <Input
                  {...(register &&
                    register('individualPhone' as Path<TSchema>))}
                  errorMessage={
                    errors && (errors.individualPhone?.message as string)
                  }
                  placeholder={t('common:phoneNumber')}
                  type="tel"
                />
              </div>
            </FormField>
            <FormField
              text={t('common:addressLine1')}
              textWrapperClassName="mt-3"
            >
              <div className="flex flex-1 flex-wrap items-start gap-5">
                <Input
                  {...(register &&
                    register('individualAddressLine1' as Path<TSchema>))}
                  errorMessage={
                    errors && (errors.individualAddressLine1?.message as string)
                  }
                  placeholder={t('common:addressLine1')}
                  type="search"
                />
              </div>
            </FormField>
            <FormField
              text={t('common:addressLine2')}
              textWrapperClassName="mt-3"
            >
              <div className="flex flex-1 flex-wrap items-start gap-5">
                <Input
                  {...(register &&
                    register('individualAddressLine2' as Path<TSchema>))}
                  errorMessage={
                    errors && (errors.individualAddressLine2?.message as string)
                  }
                  placeholder={t('common:addressLine2')}
                  type="search"
                />
              </div>
            </FormField>
            <FormField
              text={t('common:postalCode')}
              textWrapperClassName="mt-3"
            >
              <div className="flex flex-1 flex-wrap items-start gap-5">
                <Input
                  {...(register &&
                    register('individualPostalCode' as Path<TSchema>))}
                  errorMessage={
                    errors && (errors.individualPostalCode?.message as string)
                  }
                  placeholder={t('common:postalCode')}
                  type="search"
                />
              </div>
            </FormField>
            <FormField text={t('common:city')} textWrapperClassName="mt-3">
              <div className="flex flex-1 flex-wrap items-start gap-5">
                <Input
                  {...(register && register('individualCity' as Path<TSchema>))}
                  errorMessage={
                    errors && (errors.individualCity?.message as string)
                  }
                  placeholder={t('common:city')}
                  type="search"
                />
              </div>
            </FormField>
            <FormField text={t('common:country')} textWrapperClassName="mt-3">
              <div className="flex flex-1 flex-wrap items-start gap-5">
                <Input
                  {...(register &&
                    register('individualCountry' as Path<TSchema>))}
                  errorMessage={
                    errors && (errors.individualCountry?.message as string)
                  }
                  placeholder={t('common:country')}
                  type="search"
                />
              </div>
            </FormField>
            <hr className="mt-7 mb-5" />
            <FormSectionTitle
              title={t('sendingClientRequestByEmail')}
              titleClassName="font-semibold"
            />
            <FormField
              text={`${t('sendTheRequestByEmail')} ?`}
              textWrapperClassName="mt-2"
            >
              <div className="flex flex-1 flex-wrap items-start gap-5">
                <Switch
                  {...(register && register('sendByValue' as Path<TSchema>))}
                  errorMessage={
                    errors && (errors.sendByValue?.message as string)
                  }
                  onChange={() => setToSendByEmail(!toSendByEmail)}
                  checked={toSendByEmail}
                />
              </div>
            </FormField>
            {toSendByEmail && (
              <>
                <FormField
                  text={t('common:emailSubject')}
                  required
                  textWrapperClassName="mt-5"
                >
                  <div className="flex flex-1 flex-wrap items-start gap-5 mt-2">
                    <Input
                      {...(register &&
                        register('individualEmailSubject' as Path<TSchema>))}
                      errorMessage={
                        errors &&
                        (errors.individualEmailSubject?.message as string)
                      }
                      type="search"
                      placeholder={t('common:emailSubject')}
                    />
                  </div>
                </FormField>
                <FormField
                  text={t('common:emailContent')}
                  required
                  textWrapperClassName="mt-3"
                >
                  <div className="flex flex-1 flex-wrap items-start gap-5">
                    <Controller
                      name="individualEmailContent"
                      control={control}
                      render={({ field }) => (
                        <RichTextEditor
                          editorClassName={twMerge('')}
                          errorMessage={
                            errors &&
                            (errors.individualEmailContent?.message as string)
                          }
                          value={field.value}
                          onChange={field.onChange}
                        />
                      )}
                    />
                  </div>
                </FormField>
              </>
            )}
          </>
        </Card>
      </form>
      <Modal
        id="information-modal"
        title={t('common:information')}
        centerY
        showCancelBtn={false}
        showSecondBtn={true}
        secondBtnLabel="Ok"
        size="small"
      >
        <WarningMessage
          message={warningMessage}
          containerClassName="min-w-[278px]"
        />
      </Modal>
      {(isPendingUpdateVehicule ||
        isPendingCreateVehicule ||
        isLoadingGroup ||
        isLoadingCompany ||
        isLoadingBranch) && <Loader />}
    </>
  );
};
